/**
 * WasmStateSnapshot - Capture comprehensive WASM state on errors
 *
 * This module captures the full WASM typesetter state when errors occur,
 * providing AI-ready context for debugging. The snapshot includes:
 * - Document state (text, hash, version)
 * - Paragraph layout information
 * - Page structure and boundaries
 * - Recent operations leading to the error
 * - Memory and performance metrics
 *
 * @example
 * try {
 *   await typesetterClient.applyEditDelta(delta);
 * } catch (error) {
 *   const snapshot = WasmStateCapture.captureOnError(error, typesetterClient, {
 *     operation: 'applyEditDelta',
 *     delta,
 *     userAction: 'typing'
 *   });
 *   WasmLogger.error('typesetter', error, { stateSnapshot: snapshot });
 * }
 */

import { WeBelieveLogger, captureFullStack } from '../WeBelieveLogger';
import type { SourceLocation } from '../WeBelieveLogger.types';

/**
 * Paragraph state at time of snapshot
 */
export interface ParagraphState {
  id: string;
  graphemeStart: number;
  graphemeEnd: number;
  lineCount: number;
  heightFp?: number;
  pageIndices: number[];
}

/**
 * Page state at time of snapshot
 */
export interface PageState {
  pageIndex: number;
  lineCount: number;
  firstGrapheme: number;
  lastGrapheme: number;
  hash?: string;
  paragraphIds: string[];
}

/**
 * Edit operation that was being performed
 */
export interface EditContext {
  position: number;
  deleteCount: number;
  insertLength: number;
  insertPreview: string; // First 50 chars
  oldTextLength: number;
  newTextLength: number;
  affectedParagraphs: string[];
}

/**
 * Complete WASM state snapshot for AI debugging
 */
export interface WasmStateSnapshot {
  // Timestamp and error info
  timestamp: string;
  errorMessage: string;
  errorStack?: string;
  operation: string;
  userAction?: string;

  // Document state
  document: {
    hash: string;
    textLength: number;
    textPreview: string;      // First/last 100 chars
    textTail: string;         // Last 100 chars
    version: number;
    formFeedCount: number;
    formFeedPositions: number[];
  };

  // Paragraph structure
  paragraphs: {
    count: number;
    items: ParagraphState[];
    hasHeightIndex: boolean;
  };

  // Page structure
  pages: {
    totalCount: number;
    cached: PageState[];
    hashesStored: number;
  };

  // Cache state
  cache: {
    pageCacheSize: number;
    snapshotCacheSize: number;
    paragraphToPageMapSize: number;
    sentFontsCount: number;
  };

  // Edit context (if this was an edit operation)
  editContext?: EditContext;

  // Worker state
  worker: {
    isReady: boolean;
    isInitialized: boolean;
    initInFlight: boolean;
    editInFlight: number;
    isPaginating: boolean;
    consecutiveCrashes: number;
    wasmCrashed: boolean;
  };

  // Performance metrics
  performance: {
    heapUsed?: number;
    heapTotal?: number;
    lastPaginationFaultMs?: number;
  };

  // Source location (uses WeBelieveLogger's SourceLocation format)
  source: SourceLocation;
}

/**
 * Lightweight snapshot for non-critical logging
 */
export interface WasmStateSummary {
  timestamp: string;
  operation: string;
  docHash: string;
  textLength: number;
  paragraphCount: number;
  pageCount: number;
  version: number;
  workerReady: boolean;
}

/**
 * Interface for TypesetterWorkerClient state access
 * This allows us to capture state without tight coupling
 */
export interface TypesetterStateAccessor {
  // Document state
  getCurrentDocHash(): string;
  getCurrentDocumentText(): string;
  getLastInitializedText(): string;
  getCurrentDocumentVersion(): number;
  getCurrentTotalPages(): number;

  // Paragraph state
  getDocumentParagraphs(): Array<{ id: string; graphemeStart: number; graphemeEnd: number; version: number }>;
  getCachedParagraphSnapshots(): Array<{
    paragraph_id?: string;
    snapshot_id?: string;
    lines?: Array<{ start_grapheme_index: number; end_grapheme_index: number }>;
    total_height_fp?: number;
  }>;
  getParagraphToPageMap(): Map<string, number[]>;

  // Page state
  getPageCache(): Map<string, { lines?: unknown[] }>;
  getPageHashes(): Map<number, string>;

  // Worker state
  isWorkerReady(): boolean;
  isDocumentInitialized(): boolean;
  isInitInFlight(): boolean;
  getEditInFlightCount(): number;
  getIsPaginating(): boolean;
  getConsecutiveWasmCrashes(): number;
  isWasmCrashed(): boolean;

  // Optional
  getSentFontsCount?(): number;
  getLastPaginationFaultMs?(): number;
  getHeightIndexManager?(): { getParagraphCount(): number } | null;
}

/**
 * WasmStateCapture - Static utility for capturing WASM state snapshots
 */
export const WasmStateCapture = {
  /**
   * Capture full state snapshot on error
   */
  captureOnError(
    error: Error,
    accessor: TypesetterStateAccessor,
    context: {
      operation: string;
      userAction?: string;
      editDelta?: { position: number; deleteCount: number; insertText: string };
      oldTextLength?: number;
      newTextLength?: number;
      affectedParagraphs?: string[];
    }
  ): WasmStateSnapshot {
    const source = captureFullStack();
    const docText = accessor.getCurrentDocumentText() || accessor.getLastInitializedText() || '';
    const formFeedPositions: number[] = [];
    for (let i = 0; i < docText.length; i++) {
      if (docText[i] === '\f') formFeedPositions.push(i);
    }

    // Build paragraph state
    const paragraphItems: ParagraphState[] = [];
    const docParagraphs = accessor.getDocumentParagraphs() || [];
    const snapshots = accessor.getCachedParagraphSnapshots() || [];
    const paraToPage = accessor.getParagraphToPageMap();

    for (const para of docParagraphs) {
      const snapshot = snapshots.find(s =>
        (s.paragraph_id === para.id) || (s.snapshot_id === para.id)
      );
      paragraphItems.push({
        id: para.id.slice(0, 12),
        graphemeStart: para.graphemeStart,
        graphemeEnd: para.graphemeEnd,
        lineCount: snapshot?.lines?.length ?? 0,
        heightFp: snapshot?.total_height_fp,
        pageIndices: paraToPage.get(para.id) || []
      });
    }

    // Build page state
    const pageCache = accessor.getPageCache();
    const pageHashes = accessor.getPageHashes();
    const cachedPages: PageState[] = [];

    pageCache.forEach((page, key) => {
      const pageIndex = parseInt(key.split('-').pop() || '0');
      const lines = (page.lines || []) as Array<{ start_grapheme_index: number; end_grapheme_index: number }>;
      cachedPages.push({
        pageIndex,
        lineCount: lines.length,
        firstGrapheme: lines[0]?.start_grapheme_index ?? 0,
        lastGrapheme: lines[lines.length - 1]?.end_grapheme_index ?? 0,
        hash: pageHashes.get(pageIndex),
        paragraphIds: [] // Would need more complex extraction
      });
    });
    cachedPages.sort((a, b) => a.pageIndex - b.pageIndex);

    // Build edit context if applicable
    let editContext: EditContext | undefined;
    if (context.editDelta) {
      editContext = {
        position: context.editDelta.position,
        deleteCount: context.editDelta.deleteCount,
        insertLength: context.editDelta.insertText.length,
        insertPreview: context.editDelta.insertText.slice(0, 50),
        oldTextLength: context.oldTextLength ?? 0,
        newTextLength: context.newTextLength ?? 0,
        affectedParagraphs: (context.affectedParagraphs || []).map(id => id.slice(0, 12))
      };
    }

    return {
      timestamp: new Date().toISOString(),
      errorMessage: error.message,
      errorStack: error.stack,
      operation: context.operation,
      userAction: context.userAction,

      document: {
        hash: accessor.getCurrentDocHash().slice(0, 16),
        textLength: docText.length,
        textPreview: docText.slice(0, 100).replace(/\n/g, '\\n').replace(/\f/g, '\\f'),
        textTail: docText.slice(-100).replace(/\n/g, '\\n').replace(/\f/g, '\\f'),
        version: accessor.getCurrentDocumentVersion(),
        formFeedCount: formFeedPositions.length,
        formFeedPositions: formFeedPositions.slice(0, 20) // Limit for large docs
      },

      paragraphs: {
        count: docParagraphs.length,
        items: paragraphItems.slice(0, 50), // Limit for large docs
        hasHeightIndex: !!accessor.getHeightIndexManager?.()
      },

      pages: {
        totalCount: accessor.getCurrentTotalPages(),
        cached: cachedPages.slice(0, 50), // Limit for large docs
        hashesStored: pageHashes.size
      },

      cache: {
        pageCacheSize: pageCache.size,
        snapshotCacheSize: snapshots.length,
        paragraphToPageMapSize: paraToPage.size,
        sentFontsCount: accessor.getSentFontsCount?.() ?? 0
      },

      editContext,

      worker: {
        isReady: accessor.isWorkerReady(),
        isInitialized: accessor.isDocumentInitialized(),
        initInFlight: accessor.isInitInFlight(),
        editInFlight: accessor.getEditInFlightCount(),
        isPaginating: accessor.getIsPaginating(),
        consecutiveCrashes: accessor.getConsecutiveWasmCrashes(),
        wasmCrashed: accessor.isWasmCrashed()
      },

      performance: {
        heapUsed: (performance as any).memory?.usedJSHeapSize,
        heapTotal: (performance as any).memory?.totalJSHeapSize,
        lastPaginationFaultMs: accessor.getLastPaginationFaultMs?.()
      },

      source
    };
  },

  /**
   * Capture lightweight summary for non-critical logging
   */
  captureSummary(
    accessor: TypesetterStateAccessor,
    operation: string
  ): WasmStateSummary {
    const docText = accessor.getCurrentDocumentText() || accessor.getLastInitializedText() || '';

    return {
      timestamp: new Date().toISOString(),
      operation,
      docHash: accessor.getCurrentDocHash().slice(0, 16),
      textLength: docText.length,
      paragraphCount: accessor.getDocumentParagraphs()?.length ?? 0,
      pageCount: accessor.getCurrentTotalPages(),
      version: accessor.getCurrentDocumentVersion(),
      workerReady: accessor.isWorkerReady()
    };
  },

  /**
   * Log error with full state snapshot to WeBelieveLogger
   */
  logErrorWithSnapshot(
    error: Error,
    accessor: TypesetterStateAccessor,
    context: {
      operation: string;
      userAction?: string;
      editDelta?: { position: number; deleteCount: number; insertText: string };
      oldTextLength?: number;
      newTextLength?: number;
      affectedParagraphs?: string[];
    }
  ): WasmStateSnapshot {
    const snapshot = this.captureOnError(error, accessor, context);

    // Log to WeBelieveLogger with full context
    WeBelieveLogger.logWithSource(
      'error',
      `wasm.${context.operation}.fail`,
      error.message,
      snapshot.source,
      {
        stateSnapshot: snapshot,
        errorStack: error.stack
      }
    );

    return snapshot;
  }
};

/**
 * Create a TypesetterStateAccessor from a TypesetterWorkerClient instance
 * This is a factory function to avoid circular dependencies
 */
export function createStateAccessor(client: any): TypesetterStateAccessor {
  return {
    getCurrentDocHash: () => client.currentDocHash || '',
    getCurrentDocumentText: () => client.currentDocumentText || '',
    getLastInitializedText: () => client.lastInitializedText || '',
    getCurrentDocumentVersion: () => client.currentDocumentVersion || 0,
    getCurrentTotalPages: () => client.currentTotalPages || 0,

    getDocumentParagraphs: () => client.documentParagraphs || [],
    getCachedParagraphSnapshots: () => client.cachedParagraphSnapshots || [],
    getParagraphToPageMap: () => client.paragraphToPageMap || new Map(),

    getPageCache: () => client.pageCache || new Map(),
    getPageHashes: () => client.pageHashes || new Map(),

    isWorkerReady: () => client.isReady || false,
    isDocumentInitialized: () => client.documentInitialized || false,
    isInitInFlight: () => !!client.initializeInFlight,
    getEditInFlightCount: () => client.editInFlightCount || 0,
    getIsPaginating: () => client.isPaginating || false,
    getConsecutiveWasmCrashes: () => client.consecutiveWasmCrashes || 0,
    isWasmCrashed: () => client.wasmCrashed || false,

    getSentFontsCount: () => client.sentFonts?.size || 0,
    getLastPaginationFaultMs: () => client.lastPaginationFaultAtMs || 0,
    getHeightIndexManager: () => client.heightIndexManager || null
  };
}
